import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { PCMP_REQUEST_DEFAULT_VIEW } from '../constants/pcmpRequest.constants';
import type {
  PcmpRequestBatchesParams,
  PcmpRequestQueueParams,
  PcmpRequestQueueSearch,
  PcmpRequestStatus,
  PcmpRequestView,
  PcmpUrgentRequestsParams,
} from '../types/pcmpRequest.types';
import { usePcmpQueueFilters } from './usePcmpQueueFilters';

const route = getRouteApi('/_auth/pcmp/requests/');

/** The request screen's URL state: tab, My Requests status, queue filters and paging. */
export function usePcmpRequestFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const setSearch = useCallback(
    (update: (prev: PcmpRequestQueueSearch) => PcmpRequestQueueSearch) => {
      void navigate({ search: update });
    },
    [navigate],
  );

  const queue = usePcmpQueueFilters(search, setSearch);
  const { appliedFilters, page, limit } = queue;

  const tab = search.tab ?? PCMP_REQUEST_DEFAULT_VIEW;
  const requestStatus = search.requestStatus;
  // My Requests and Urgent Requests narrow by request category (LDS-768).
  const requestCategory =
    tab === 'MINE' || tab === 'URGENT_REQUESTS' ? search.requestCategory : undefined;

  /** The proposal list's params; null on the Batches and Urgent Requests tabs. */
  const queueParams = useMemo<PcmpRequestQueueParams | null>(
    () =>
      tab === 'BATCHES' || tab === 'URGENT_REQUESTS'
        ? null
        : {
            ...appliedFilters,
            tab,
            requestStatus: tab === 'MINE' ? requestStatus : undefined,
            requestCategory,
            page,
            limit,
          },
    [appliedFilters, tab, requestStatus, requestCategory, page, limit],
  );

  const batchesParams = useMemo<PcmpRequestBatchesParams>(
    () => ({ ...appliedFilters, page, limit }),
    [appliedFilters, page, limit],
  );

  const urgentRequestsParams = useMemo<PcmpUrgentRequestsParams>(
    () => ({ ...appliedFilters, requestCategory, page, limit }),
    [appliedFilters, requestCategory, page, limit],
  );

  const setTab = useCallback(
    (next: PcmpRequestView) => {
      setSearch((prev) => ({
        ...prev,
        tab: next === PCMP_REQUEST_DEFAULT_VIEW ? undefined : next,
        requestStatus: undefined,
        requestCategory: undefined,
        page: undefined,
      }));
    },
    [setSearch],
  );

  const setRequestCategory = useCallback(
    (next: string | undefined) => {
      setSearch((prev) => ({ ...prev, requestCategory: next, page: undefined }));
    },
    [setSearch],
  );

  const setRequestStatus = useCallback(
    (next: PcmpRequestStatus | undefined) => {
      setSearch((prev) => ({ ...prev, requestStatus: next, page: undefined }));
    },
    [setSearch],
  );

  return {
    ...queue,
    tab,
    requestStatus,
    requestCategory,
    queueParams,
    batchesParams,
    urgentRequestsParams,
    setTab,
    setRequestStatus,
    setRequestCategory,
  };
}

export type PcmpRequestFilterController = ReturnType<typeof usePcmpRequestFilters>;
